import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import AppNav from '@/components/AppNav';
import { changelogApi, ChangelogEntry, getTypeColor, getTypeLabel } from '@/lib/changelogApi';
import { itemApi, Item } from '@/lib/itemApi';
import { Select, SelectItem } from '@fyos/components';
import ConfirmDialog from '@/components/ConfirmDialog';

const ENTRY_TYPES = [
  { value: 'new', label: 'New', color: '#10B981' },
  { value: 'improved', label: 'Improved', color: '#3B82F6' },
  { value: 'fixed', label: 'Fixed', color: '#F59E0B' },
  { value: 'removed', label: 'Removed', color: '#EF4444' },
  { value: 'security', label: 'Security', color: '#8B5CF6' },
];

export default function ChangelogPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaceStore();
  
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [shippedItems, setShippedItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [entryType, setEntryType] = useState<'new' | 'improved' | 'fixed' | 'removed' | 'security'>('new');
  const [version, setVersion] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [linkedItemId, setLinkedItemId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) {
      fetchData();
    }
  }, [workspaceId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesData, itemsData] = await Promise.all([
        changelogApi.getAll(workspaceId!),
        itemApi.getAll(workspaceId!, 'shipped'),
      ]);
      setEntries(entriesData);
      setShippedItems(itemsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const entry = await changelogApi.create(workspaceId!, {
        title,
        content,
        entry_type: entryType,
        version: version || undefined,
        release_date: releaseDate || undefined,
        item_id: linkedItemId || undefined,
      });
      setEntries([entry, ...entries]);
      setShowCreateModal(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create changelog entry');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const updated = await changelogApi.publish(workspaceId!, id);
      setEntries(entries.map(e => e.id === id ? updated : e));
    } catch (err) {
      console.error('Failed to publish entry:', err);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteEntryId(id);
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntryId) return;
    try {
      await changelogApi.delete(workspaceId!, deleteEntryId);
      setEntries(entries.filter(e => e.id !== deleteEntryId));
    } catch (err) {
      console.error('Failed to delete entry:', err);
    } finally {
      setDeleteEntryId(null);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setEntryType('new');
    setVersion('');
    setReleaseDate('');
    setLinkedItemId(null);
    setError('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AppNav />

      {/* Page header */}
      <div className="py-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/roadmap/${workspaceId}`)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ← Back to board
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{currentWorkspace?.name} — Changelog</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
            >
              New Entry
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Loading changelog...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow dark:bg-gray-800">
            <p className="text-gray-600 dark:text-gray-400 mb-4">No changelog entries yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700"
            >
              Create Your First Entry
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {entries.map(entry => (
              <div key={entry.id} className="bg-white rounded-lg shadow p-6 dark:bg-gray-800">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span
                        className="px-3 py-1 rounded text-sm font-medium text-white"
                        style={{ backgroundColor: getTypeColor(entry.entry_type) }}
                      >
                        {getTypeLabel(entry.entry_type)}
                      </span>
                      {entry.version && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm dark:bg-gray-700 dark:text-gray-300">
                          v{entry.version}
                        </span>
                      )}
                      {entry.is_published ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm dark:bg-green-900/30 dark:text-green-400">
                          Published
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm dark:bg-yellow-900/30 dark:text-yellow-400">
                          Draft
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{entry.title}</h2>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-3">{entry.content}</p>
                    {entry.item && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Linked to: <span className="font-medium">{entry.item.title}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => handlePublish(entry.id)}
                      className={`px-3 py-1 text-sm rounded ${
                        entry.is_published
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {entry.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-4">
                    {entry.author && (
                      <span>By {entry.author.name}</span>
                    )}
                    {entry.published_at && (
                      <span>{formatDate(entry.published_at)}</span>
                    )}
                  </div>
                  {entry.release_date && (
                    <span>Release: {formatDate(entry.release_date)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-gray-800">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Create Changelog Entry</h3>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Entry Type *
                </label>
                <div className="flex flex-wrap gap-2">
                  {ENTRY_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setEntryType(type.value as any)}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        entryType === type.value
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                      style={entryType === type.value ? { backgroundColor: type.color } : {}}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="What's new?"
                  disabled={creating}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Content *
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Describe the changes..."
                  rows={5}
                  disabled={creating}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Version
                  </label>
                  <input
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="1.0.0"
                    disabled={creating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Release Date
                  </label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-hidden focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    disabled={creating}
                  />
                </div>
              </div>

              {shippedItems.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Link to Shipped Item
                  </label>
                  <Select
                    value={linkedItemId || '__none__'}
                    onValueChange={(val) => setLinkedItemId(val === '__none__' ? null : val)}
                    disabled={creating}
                    placeholder="Select shipped item..."
                  >
                    <SelectItem value="__none__">None</SelectItem>
                    {shippedItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>{item.title}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded dark:bg-red-950/30 dark:border-red-800">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!deleteEntryId}
        onOpenChange={(open) => { if (!open) setDeleteEntryId(null); }}
        title="Delete Changelog Entry"
        description="Are you sure you want to delete this changelog entry?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDeleteEntry}
      />
    </div>
  );
}
